import { useCallback, useMemo, lazy, Suspense, useEffect } from 'react';
import { useNotifier } from '@picsart/notifier/Notifier';
import { useTranslator } from '@picsart/rc/services/localization';
import { NotificationStatuses, NotifierDuration, ToastType } from '@picsart/notifier';

import { DataActionTypes } from 'pages/Batch/sections/Context';
import useBatchData from 'pages/Batch/sections/Context/useBatchData';
import { DOWNLOAD_SURVEY_OPENED_LOCATIONS, MODAL_MESSAGES, SURVEY_AFTER_BATCH_EDITOR_DOWNLOAD, SURVEY_MONKEY_URLS } from 'configs/constants';
import Cookies from 'services/wrappers/cookies';
import useMemoSelector from 'hooks/useMemoSelector';
import { getUserData } from 'store/selectors/general';
import { IMAGE_MIME_TYPES } from 'pages/Batch/configs';
import BatchDataService from 'pages/Batch/sections/Context/BatchDataService';

const SuccessModal = lazy(() => import('components/common/Modals/SuccessModal'));

function SuccessNotificationUI() {
  const { dispatchData, isSuccessModal, setIsSuccessModal, isZipFile, isWeb, imageFormat, data } = useBatchData();
  const { userLocation } = useMemoSelector(getUserData);
  const { addNotification } = useNotifier();
  const translate = useTranslator();

  const closeModal = useCallback(() => {
    setIsSuccessModal(false);
  }, [setIsSuccessModal]);

  const resetEditor = useCallback(() => {
    dispatchData({ type: DataActionTypes.DELETE_ALL_DATA });
    closeModal();
    isWeb && data.forEach(({ key }) => BatchDataService.removeData(key));
  }, [closeModal, dispatchData, data, isWeb]);

  useEffect(() => {
    if (isSuccessModal) {
      addNotification({
        toastType: ToastType.Notification,
        status: NotificationStatuses.Success,
        onClose: () => {
          setIsSuccessModal(false);
        },
        title: translate(MODAL_MESSAGES.mobileImageWasDownloadedTitle),
        description: translate(MODAL_MESSAGES.mobileImageWasDownloadedContent(imageFormat === IMAGE_MIME_TYPES.JPEG ? 'JPG' : 'PNG')),
        duration: NotifierDuration.Notification,
      });
    }
  }, [addNotification, imageFormat, isSuccessModal, setIsSuccessModal, translate]);

  const onSurveyClick = useCallback(() => {
    Cookies.set(SURVEY_AFTER_BATCH_EDITOR_DOWNLOAD, 'opened');
  }, []);

  const surveyLink = useMemo(() => {
    const isBatchEditorSurveyOpened = !!Cookies.get(SURVEY_AFTER_BATCH_EDITOR_DOWNLOAD);

    return isBatchEditorSurveyOpened ? SURVEY_MONKEY_URLS.batchEditorSecondSurvey : SURVEY_MONKEY_URLS.batchEditorFirstSurvey;
  }, []);

  return isWeb
    ? (
      <Suspense fallback={null}>
        <SuccessModal
          isOpen={isSuccessModal}
          description={isZipFile ? MODAL_MESSAGES.zipWasDownloaded : MODAL_MESSAGES.imageWasDownloaded}
          onContinue={closeModal}
          onCreateNew={resetEditor}
          showSurvey={!!userLocation && DOWNLOAD_SURVEY_OPENED_LOCATIONS.includes(userLocation)}
          onSurveyClick={onSurveyClick}
          surveyLink={surveyLink}
          showClose
        />
      </Suspense>
    ) : null;
}

export default SuccessNotificationUI;
